import React, { useEffect, useState } from 'react';
import type { ComparisonScope, MarketProfileState } from '../marketProfileStore';
import {
  componentMarketStatus,
  compensationHeadline,
  computeTotalComp,
  formatActualDate,
  formatRub,
  hasRoleMismatch,
  isInsufficientSample,
  isProfileOutdated,
  marketDataInsight,
  percentileToLabel,
  rolesForGradingLadder,
  stablePercentileForScope,
  stableSampleSizeForScope,
  type ComponentMarketStatus,
  positionFixValue,
  positionHasLtip,
  positionLtipAmountValue,
  positionStiValue,
} from '../marketProfileStore';
import { SensitiveDataNotice } from '../MarketProfileShared';
import { fetchCommunityGradingTreeCached } from '../../../../services/gradingService';
import { normalizeGradingTreePayload } from '../../../Workspace/admin/grading/utils';
import type { GradingTreePayload } from '../../../Workspace/admin/grading/types';

type Props = {
  state: MarketProfileState;
  confirmed: boolean;
  onConfirmReview?: () => void;
};

function statusBadgeClass(status: ComponentMarketStatus | ReturnType<typeof percentileToLabel>): string {
  if (status === 'ниже рынка') return 'below';
  if (status === 'в рынке') return 'in';
  if (status === 'выше рынка' || status === 'выше медианы') return 'above';
  return 'muted';
}

function ComparisonScopeToggle({
  scope,
  onChange,
}: {
  scope: ComparisonScope;
  onChange: (scope: ComparisonScope) => void;
}) {
  return (
    <div className="dcf-mp-md-scope">
      <p className="dcf-mp-md-scope__label">Группа сравнения</p>
      <div className="dcf-mp-option-grid dcf-mp-md-scope__grid">
        <button
          type="button"
          className={`dcf-mp-option-card ${scope === 'industry' ? 'dcf-mp-option-card--active' : ''}`}
          onClick={() => onChange('industry')}
        >
          <span className="dcf-mp-md-scope__title">Вся инвестиционная индустрия</span>
          <span className="dcf-mp-md-scope__desc">
            Сравнение с релевантными ролями по всей инвестиционной индустрии
          </span>
        </button>
        <button
          type="button"
          className={`dcf-mp-option-card ${scope === 'sector' ? 'dcf-mp-option-card--active' : ''}`}
          onClick={() => onChange('sector')}
        >
          <span className="dcf-mp-md-scope__title">Мой сектор</span>
          <span className="dcf-mp-md-scope__desc">
            Сравнение только с участниками из вашего сектора
          </span>
        </button>
      </div>
    </div>
  );
}

function QuartileChart({ percentile }: { percentile: number }) {
  const markers = [
    { pos: 25, label: 'P25' },
    { pos: 50, label: 'P50' },
    { pos: 75, label: 'P75' },
    { pos: 90, label: 'P90' },
  ];
  return (
    <div className="dcf-mp-quartile">
      <div className="dcf-mp-quartile__track">
        {markers.map((m) => (
          <div key={m.label} className="dcf-mp-quartile__tick" style={{ left: `${m.pos}%` }}>
            <span className="dcf-mp-quartile__tick-line" />
            <span className="dcf-mp-quartile__tick-label">{m.label}</span>
          </div>
        ))}
        <div className="dcf-mp-quartile__user" style={{ left: `${percentile}%` }} title="Ваше положение">
          <span className="dcf-mp-quartile__user-dot" />
          <span className="dcf-mp-quartile__user-label">Вы</span>
        </div>
      </div>
      <div className="dcf-mp-quartile__legend">
        <span>нижний квартиль</span>
        <span>медиана</span>
        <span>верхний квартиль</span>
        <span>высокий диапазон</span>
      </div>
    </div>
  );
}

function ComponentRow({
  label,
  value,
  status,
}: {
  label: string;
  value: string;
  status: ComponentMarketStatus;
}) {
  const fill = status === 'недостаточно данных' ? 0 : status === 'ниже рынка' ? 28 : status === 'в рынке' ? 52 : 72;
  return (
    <div className="dcf-mp-comp-row">
      <div className="dcf-mp-comp-row__head">
        <span className="dcf-mp-comp-row__name">{label}</span>
        <span className={`dcf-mp-comp-row__badge dcf-mp-comp-row__badge--${statusBadgeClass(status)}`}>
          {status}
        </span>
      </div>
      <div className="dcf-mp-comp-row__value">{value}</div>
      {status !== 'недостаточно данных' && (
        <div className="dcf-mp-comp-row__bar">
          <div className="dcf-mp-comp-row__fill" style={{ width: `${fill}%` }} />
        </div>
      )}
    </div>
  );
}

function GradingLadder({ state, tree }: { state: MarketProfileState; tree: GradingTreePayload | null }) {
  const roles = rolesForGradingLadder(state, tree);
  const userLevel = state.grading.result?.gradeLevel ?? null;
  const userRole = state.grading.result?.roleName ?? null;

  return (
    <ol className="dcf-mp-grade-ladder" aria-label="Уровни ролей в секторе">
      {roles.map((role) => {
        const isUser = userLevel != null
          ? role.gradeLevel === userLevel
          : role.name === userRole;
        return (
          <li
            key={role.name}
            className={`dcf-mp-grade-ladder__item ${isUser ? 'dcf-mp-grade-ladder__item--active' : ''}`}
          >
            <span className="dcf-mp-grade-ladder__name">{role.name}</span>
            {isUser && <span className="dcf-mp-grade-ladder__you">ваш уровень</span>}
          </li>
        );
      })}
    </ol>
  );
}

export default function MarketDataOpenDashboard({
  state,
  confirmed,
  onConfirmReview,
}: Props) {
  const [scope, setScope] = useState<ComparisonScope>('sector');
  const [gradingTree, setGradingTree] = useState<GradingTreePayload | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchCommunityGradingTreeCached()
      .then((data) => {
        if (!cancelled) setGradingTree(normalizeGradingTreePayload(data));
      })
      .catch(() => {
        if (!cancelled) setGradingTree(null);
      });
    return () => { cancelled = true; };
  }, []);

  const { data } = state.position;
  const grading = state.grading.result;
  const percentile = stablePercentileForScope(state, scope);
  const label = percentileToLabel(percentile);
  const sampleSize = stableSampleSizeForScope(state, scope);
  const outdated = isProfileOutdated(state);
  const insufficient = isInsufficientSample(sampleSize) && scope === 'sector';
  const total = computeTotalComp(data);
  const actualDate = formatActualDate(data.actualDate);
  const mismatch = hasRoleMismatch(state);

  const scopeLabel = scope === 'industry'
    ? 'Вся инвестиционная индустрия'
    : data.sectorName || 'Мой сектор';

  const statusBadge = outdated
    ? 'Требуется обновление данных'
    : confirmed
      ? 'Данные открыты'
      : 'Предварительный просмотр';

  return (
    <div className="dcf-mp-md-open">
      {/* Summary */}
      <section className="dcf-mp-card dcf-mp-md-summary">
        <div className="dcf-mp-md-summary__main">
          <h1 className="dcf-mp-hero__title">Рыночные данные</h1>
          <p className="dcf-mp-hero__desc">
            Сравните компенсацию и уровень роли с релевантной рыночной группой.
          </p>
          <p className="dcf-mp-md-summary__note">
            Расчёт основан на данных вашей роли, компенсации и результатах грейдинга.
          </p>
        </div>
        <span className={`dcf-mp-access-badge ${confirmed && !outdated ? 'dcf-mp-access-badge--open' : ''}`}>
          <i className={`fas ${confirmed && !outdated ? 'fa-circle-check' : outdated ? 'fa-clock-rotate-left' : 'fa-chart-line'}`} aria-hidden="true" />
          {statusBadge}
        </span>
      </section>

      {!confirmed && onConfirmReview && (
        <div className="dcf-mp-notice dcf-mp-notice--ready">
          <i className="fas fa-circle-check" aria-hidden="true" />
          <span>Анкеты заполнены. Подтвердите данные, чтобы зафиксировать рыночные ориентиры.</span>
          <button type="button" className="dcf-btn dcf-btn--primary" onClick={onConfirmReview}>
            Проверить и подтвердить
          </button>
        </div>
      )}

      {outdated && (
        <div className="dcf-mp-notice dcf-mp-notice--outdated">
          <i className="fas fa-clock-rotate-left" aria-hidden="true" />
          <span>
            С момента последнего обновления прошло достаточно времени. Для актуализации данных
            обратитесь к администратору сообщества.
          </span>
        </div>
      )}

      <ComparisonScopeToggle scope={scope} onChange={setScope} />

      {insufficient && (
        <section className="dcf-mp-card dcf-mp-md-insufficient">
          <h2 className="dcf-mp-card__title">Недостаточно данных для точного сравнения</h2>
          <p className="dcf-mp-action-card__desc">
            В выбранной группе пока недостаточно профилей. Мы можем показать более широкий
            ориентир по всей инвестиционной индустрии или предложить изменить группу сравнения.
          </p>
          <div className="dcf-mp-md-insufficient__actions">
            <button type="button" className="dcf-btn dcf-btn--secondary" onClick={() => setScope('industry')}>
              Смотреть по всей инвестиционной индустрии
            </button>
          </div>
        </section>
      )}

      <div className="dcf-mp-md-layout">
        {/* Compensation dashboard */}
        <section className="dcf-mp-card dcf-mp-md-panel dcf-mp-md-panel--wide">
          <h2 className="dcf-mp-card__title">Компенсация относительно рынка</h2>
          <p className="dcf-mp-form-hint">
            Сравнение построено по роли, уровню и выбранной группе рынка.
          </p>

          <div className="dcf-mp-result-status dcf-mp-md-status">
            <span className={`dcf-mp-market-badge dcf-mp-market-badge--${statusBadgeClass(label)}`}>
              {label}
            </span>
            <p className="dcf-mp-md-status__headline">{compensationHeadline(percentile)}</p>
            <span className="dcf-mp-result-status__note">предварительная оценка</span>
          </div>

          <QuartileChart percentile={percentile} />
          <p className="dcf-mp-percentile__value">Ориентировочный перцентиль: ~{percentile}</p>

          <div className="dcf-mp-md-comp-rows">
            <ComponentRow label="FIX" value={formatRub(positionFixValue(data))} status={componentMarketStatus(state, 'fix', scope)} />
            <ComponentRow
              label="STI"
              value={positionStiValue(data) ? `${positionStiValue(data)}% от base` : '—'}
              status={componentMarketStatus(state, 'sti', scope)}
            />
            <ComponentRow
              label="LTIP"
              value={positionHasLtip(data) ? formatRub(positionLtipAmountValue(data)) : '—'}
              status={componentMarketStatus(state, 'ltip', scope)}
            />
            <ComponentRow
              label="Total Comp"
              value={formatRub(total)}
              status={componentMarketStatus(state, 'total', scope)}
            />
          </div>

          <div className="dcf-mp-md-insight">
            <i className="fas fa-lightbulb" aria-hidden="true" />
            <span>{marketDataInsight(state, scope, sampleSize)}</span>
          </div>
        </section>

        {/* Side column */}
        <div className="dcf-mp-md-side">
          <section className="dcf-mp-card dcf-mp-md-panel">
            <h2 className="dcf-mp-card__title">С кем вы сравниваетесь</h2>
            <dl className="dcf-mp-comp-list">
              <div><dt>Режим</dt><dd>{scopeLabel}</dd></div>
              <div><dt>Индустрия</dt><dd>{data.industryName || 'Инвестиционный рынок'}</dd></div>
              <div><dt>Сектор</dt><dd>{data.sectorName || '—'}</dd></div>
              <div><dt>Роль</dt><dd>{grading?.roleName ?? data.companyRoleName ?? '—'}</dd></div>
              <div><dt>Уровень по грейдингу</dt><dd>{grading?.gradeLevel != null ? `G${grading.gradeLevel}` : '—'}</dd></div>
              <div><dt>Релевантных профилей</dt><dd>~{sampleSize}</dd></div>
              {actualDate && <div><dt>Актуальность данных</dt><dd>{actualDate}</dd></div>}
            </dl>
            {insufficient && (
              <p className="dcf-mp-card__footnote">
                В выбранной группе пока недостаточно профилей. Ориентир показан с пониженной надёжностью.
              </p>
            )}
            {!insufficient && (
              <p className="dcf-mp-card__footnote">
                Сейчас сравнение построено по группе: {scopeLabel} · {grading?.roleName ?? 'уровень роли'} · ~{sampleSize} профилей
              </p>
            )}
          </section>

          <section className="dcf-mp-card dcf-mp-md-panel">
            <h2 className="dcf-mp-card__title">Положение в грейдинговом дереве</h2>
            <p className="dcf-mp-form-hint">
              Ваш результат грейдинга внутри структуры ролей выбранного сектора.
            </p>

            <nav className="dcf-mp-hierarchy" aria-label="Структура грейдинга">
              <span>{data.industryName || 'Инвестиционный рынок'}</span>
              <i className="fas fa-chevron-right" aria-hidden="true" />
              <span>{data.sectorName || 'Сектор'}</span>
              <i className="fas fa-chevron-right" aria-hidden="true" />
              <span>{grading?.familyName ?? '—'}</span>
              <i className="fas fa-chevron-right" aria-hidden="true" />
              <span>{grading?.roleClusterName ?? '—'}</span>
              {grading?.specializationName && (
                <>
                  <i className="fas fa-chevron-right" aria-hidden="true" />
                  <span>{grading.specializationName}</span>
                </>
              )}
            </nav>

            <GradingLadder state={state} tree={gradingTree} />

            <div className="dcf-mp-md-mismatch">
              <div className="dcf-mp-md-mismatch__row">
                <span className="dcf-mp-md-mismatch__label">Формальная должность</span>
                <span className="dcf-mp-md-mismatch__value">{data.companyRoleName || '—'}</span>
              </div>
              <div className="dcf-mp-md-mismatch__row">
                <span className="dcf-mp-md-mismatch__label">Результат грейдинга</span>
                <span className="dcf-mp-md-mismatch__value">{grading?.roleName ?? '—'}</span>
              </div>
              <div className="dcf-mp-md-mismatch__row dcf-mp-md-mismatch__row--accent">
                <span className="dcf-mp-md-mismatch__label">Рыночный эквивалент</span>
                <span className="dcf-mp-md-mismatch__value">{grading?.roleName ?? '—'}</span>
              </div>
              <p className="dcf-mp-card__footnote">
                {mismatch
                  ? 'Фактическая зона ответственности может соответствовать уровню, отличному от формальной должности. Это предварительная оценка и зависит от полноты ответов.'
                  : 'Формальная должность и результат грейдинга находятся в одном диапазоне.'}
              </p>
            </div>
          </section>
        </div>
      </div>

      <SensitiveDataNotice compact />
    </div>
  );
}
